import type { LegalDocument } from "#legal-document";

/** The Terms of Service, shown in the app's legal sheets and at https://haniverse.app/terms. */
export const TERMS_OF_SERVICE_DOCUMENT: LegalDocument = {
  intro:
    'These Terms of Service ("Terms") govern your access to and use of Haniverse ("the App"), operated by ImproveLabs ("we," "us," or "our"). By downloading, accessing, or using Haniverse, you agree to these Terms.',
  lastUpdated: "November 1, 2026",
  sections: [
    {
      paragraphs: [
        "Haniverse is a Chinese character learning application that provides lessons, character reference material, spaced repetition review, and related study tools. Features may change over time as we improve the App.",
      ],
      title: "The Service",
    },
    {
      paragraphs: [
        "You may use Haniverse with or without an account. An account enables progress sync across devices. You are responsible for keeping account credentials confidential and for all activity under your account. You must provide accurate account information.",
      ],
      title: "Accounts",
    },
    {
      paragraphs: [
        "Haniverse is free to use. Haniverse Plus is an optional auto-renewing subscription, offered monthly or yearly, that unlocks every level, unlimited new characters and map missions each day, and the full mnemonics. The price and billing period are shown before you buy.",
        "For App Store purchases, payment is charged to your Apple Account when you confirm the purchase. The subscription renews automatically at the same price for the same period unless you turn off auto-renew at least 24 hours before the end of the current period; your Apple Account is charged for the renewal within 24 hours before the period ends.",
        "For Google Play purchases, payment is charged through your Google Play account at the price and billing interval shown when you subscribe. The subscription renews automatically unless cancelled through Google Play before renewal, under Google Play's subscription terms.",
        "You can manage or cancel your subscription in the store where you subscribed: Settings > Apple Account > Subscriptions on iOS, or Google Play > Payments & subscriptions > Subscriptions on Android. Cancelling stops future renewals; you keep access until the end of the period you paid for. Deleting the App or your Haniverse account does not cancel a subscription. Refunds are handled by Apple or Google under the applicable store's policies.",
        "Purchases made through the App Store are also subject to Apple’s Licensed Application End User License Agreement (https://www.apple.com/legal/internet-services/itunes/dev/stdeula/).",
        "You can restore an existing subscription on a new device from Settings > Account > Restore Purchases.",
      ],
      title: "Subscriptions",
    },
    {
      paragraphs: [
        "You agree not to misuse Haniverse. This includes attempting to break or bypass security, reverse engineer the App except where permitted by law, scrape or bulk-export content in violation of these Terms, interfere with other users or our systems, or use the App for unlawful purposes.",
      ],
      title: "Acceptable Use",
    },
    {
      paragraphs: [
        "Haniverse, including its software, design, character content, mnemonics, and branding, is owned by ImproveLabs and its licensors and is protected by applicable intellectual property laws. These Terms grant you a limited, personal, non-exclusive, non-transferable license to use the App for personal learning. You may not copy, redistribute, or create derivative works from the App or its content except as expressly allowed.",
      ],
      title: "Intellectual Property",
    },
    {
      paragraphs: [
        "Haniverse provides educational material to support learning. While we work to keep content accurate and helpful, the App is provided for study purposes only and should not be treated as professional translation, legal, or academic certification advice.",
      ],
      title: "Educational Content",
    },
    {
      paragraphs: [
        'Haniverse is provided "as is" and "as available" without warranties of any kind, whether express or implied, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement.',
      ],
      title: "Disclaimer",
    },
    {
      paragraphs: [
        "To the fullest extent permitted by law, ImproveLabs will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for loss of data, profits, or goodwill, arising from your use of Haniverse. Our total liability for any claim relating to the App is limited to the amount you paid us for the App in the twelve months before the claim, or USD $100 if you have not paid us.",
      ],
      title: "Limitation of Liability",
    },
    {
      paragraphs: [
        "You may stop using Haniverse at any time. We may suspend or terminate access if you violate these Terms or if we discontinue the App. Provisions that by their nature should survive termination will continue to apply.",
      ],
      title: "Termination",
    },
    {
      paragraphs: [
        'We may update these Terms from time to time. When we do, we will revise the "Last updated" date above. Continued use of Haniverse after changes become effective means you accept the updated Terms.',
      ],
      title: "Changes to These Terms",
    },
    {
      paragraphs: [
        "Questions about these Terms can be sent to ImproveLabs at contact@improve.dev, or through the support chat in the App and at https://haniverse.app/support.",
      ],
      title: "Contact",
    },
  ],
  title: "Terms of Service",
};
